import {
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation, useQuery } from "@apollo/client/react";
import { TextInput, Select, Text } from "@mantine/core";
import { useForm, schemaResolver } from "@mantine/form";
import { z } from "zod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/sharp-regular-svg-icons";
import DOMPurify from "dompurify";
import { gql } from "@/__generated__";
import { RecipientType } from "@/__generated__/graphql";
import Loader from "@/components/shared/loader";
import { Toast } from "design-system/src/components/toast";
import { useFormDirtyTracking } from "@/hooks/use-form-dirty-tracking";
import { US_STATE_SELECT_OPTIONS } from "shared/src/data/us-states";
import { getClientSafeErrorMessage } from "shared/src/utils/apollo-error-utils";
import styles from "./appeal-destination-form.module.scss";

const GET_CONSOLE_APPEAL_DESTINATION = gql(`
  query GetConsoleAppealDestination($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      destination {
        name
        company
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
        fax
      }
    }
    denyingPartyAppealDepartment(appealId: $appealId) {
      name
      company
      address {
        addressLine1
        addressLine2
        city
        state
        zip
      }
      fax
    }
  }
`);

const SAVE_APPEAL_DEPT_DATA = gql(`
  mutation SaveConsoleAppealDeptData($appealId: ID!, $appealDeptData: AppealDestinationInput!) {
    saveAppealDeptData(appealId: $appealId, appealDeptData: $appealDeptData)
  }
`);

export type AppealDestinationFormValues = {
  name: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  fax: string;
};

type AppealDestinationFormProps = {
  appealId: string;
  readOnly?: boolean;
};

export type AppealDestinationFormHandle = {
  submit: () => Promise<boolean>;
};

const INITIAL_VALUES: AppealDestinationFormValues = {
  name: "",
  company: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  fax: "",
};

const hasAnyValue = (values: AppealDestinationFormValues): boolean => {
  return Object.values(values).some(
    (v) => typeof v === "string" && v.trim() !== "",
  );
};

const ZIP_REGEX = /^\d{5}(?:[-\s]\d{4})?$/;
const FAX_REGEX = /^(\+?1[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/;

const appealDestinationSchema = z
  .object({
    name: z.string(),
    company: z
      .string()
      .refine(
        (v) => !v || v.trim().length <= 40,
        "Company must be 40 characters or less",
      ),
    addressLine1: z.string(),
    addressLine2: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    fax: z
      .string()
      .refine(
        (v) => !v || !v.trim() || FAX_REGEX.test(v),
        "Enter valid fax number",
      ),
  })
  .superRefine((values, ctx) => {
    if (!hasAnyValue(values)) return;

    if (!values.name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Name is required",
        path: ["name"],
      });
    } else if (values.name.trim().length > 40) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Name must be 40 characters or less",
        path: ["name"],
      });
    }

    if (!values.addressLine1.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Address line 1 is required",
        path: ["addressLine1"],
      });
    }

    if (!values.city.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "City is required",
        path: ["city"],
      });
    }

    if (!values.state.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "State is required",
        path: ["state"],
      });
    }

    if (!values.zip.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Zip code is required",
        path: ["zip"],
      });
    } else if (!ZIP_REGEX.test(values.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Valid zip code required",
        path: ["zip"],
      });
    }
  });

const AppealDestinationForm = forwardRef<
  AppealDestinationFormHandle,
  AppealDestinationFormProps
>(({ appealId, readOnly = false }, ref) => {
  const [mutationErrorMessage, setMutationErrorMessage] = useState<
    string | null
  >(null);

  const form = useForm<AppealDestinationFormValues>({
    initialValues: { ...INITIAL_VALUES },
    validate: schemaResolver(appealDestinationSchema, { sync: true }),
  });

  const { resetFormDirty } = useFormDirtyTracking(form);

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(GET_CONSOLE_APPEAL_DESTINATION, {
    variables: { appealId },
    skip: !appealId,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  const [saveAppealDeptData] = useMutation(SAVE_APPEAL_DEPT_DATA);

  useEffect(() => {
    if (queryData) {
      const destination =
        queryData.getAppealById?.destination ??
        queryData.denyingPartyAppealDepartment;

      if (destination) {
        form.setInitialValues({
          name: destination.name ?? "",
          company: destination.company ?? "",
          addressLine1: destination.address?.addressLine1 ?? "",
          addressLine2: destination.address?.addressLine2 ?? "",
          city: destination.address?.city ?? "",
          state: destination.address?.state ?? "",
          zip: destination.address?.zip ?? "",
          fax: destination.fax ?? "",
        });
        form.reset();
      }
    }
  }, [queryData]);

  const formHasValues = useMemo(() => hasAnyValue(form.values), [form.values]);

  const handleFormSubmit = async (
    values: AppealDestinationFormValues,
  ): Promise<boolean> => {
    try {
      setMutationErrorMessage(null);
      await saveAppealDeptData({
        variables: {
          appealId,
          appealDeptData: {
            recipientType: RecipientType.AppealDept,
            name: values.name,
            company: values.company || undefined,
            address: {
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2 || undefined,
              city: values.city,
              state: values.state,
              zip: values.zip,
            },
            fax: values.fax || undefined,
          },
        },
      });

      resetFormDirty();
      return true;
    } catch (error) {
      const clientSafeMessage = getClientSafeErrorMessage(error);
      setMutationErrorMessage(
        clientSafeMessage ??
          "An error occurred while saving. Please try again.",
      );
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const values = form.getValues();
      if (!hasAnyValue(values)) {
        return true;
      }

      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        return false;
      }
      return await handleFormSubmit(values);
    },
  }));

  if (queryLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader color="loader_almost_black" width="36" height="36" />
      </div>
    );
  }

  const hasNonDenyingPartyError =
    queryError &&
    (!CombinedGraphQLErrors.is(queryError) ||
      !queryError.errors.every((e) =>
        e.path?.some((p) => p === "denyingPartyAppealDepartment"),
      ));

  return (
    <>
      <Toast variant="error" isVisible={!!mutationErrorMessage}>
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(mutationErrorMessage ?? ""),
          }}
        />
      </Toast>
      {hasNonDenyingPartyError && (
        <Toast variant="error" isVisible={true}>
          An error occurred. Please try again.
        </Toast>
      )}
      <form className={styles.container} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon
                icon={faLocationDot}
                className={styles.sectionIcon}
              />
              Appeal Destination
            </h3>
            <div className={styles.sectionSubtitle}>
              Provide the mailing address or fax for the appeal
            </div>
          </div>

          {!formHasValues && (
            <Text c="dimmed" size="sm" className={styles.helperText}>
              This section is optional. Complete Insurance details first to
              auto-populate.
            </Text>
          )}

          <div className={styles.formSection}>
            <TextInput
              label="Appeal Department Name"
              maxLength={40}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-name-input"
              {...form.getInputProps("name")}
              className={styles.field}
            />

            <TextInput
              label="Company"
              maxLength={40}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-company-input"
              {...form.getInputProps("company")}
              className={styles.field}
            />

            {formHasValues && (
              <Text c="dimmed" size="sm" className={styles.helperText}>
                Address is required to save destination information.
              </Text>
            )}

            <TextInput
              label="Address Line 1"
              maxLength={64}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-address-line-1-input"
              {...form.getInputProps("addressLine1")}
              className={styles.field}
            />

            <TextInput
              label="Address Line 2"
              maxLength={64}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-address-line-2-input"
              {...form.getInputProps("addressLine2")}
              className={styles.field}
            />

            <div className={styles.fieldRow}>
              <TextInput
                label="City"
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="appeal-destination-city-input"
                {...form.getInputProps("city")}
                className={styles.field}
              />

              <Select
                label="State"
                placeholder="Select one"
                data={US_STATE_SELECT_OPTIONS}
                searchable
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="appeal-destination-state-input"
                {...form.getInputProps("state")}
                className={styles.field}
              />
            </div>

            <TextInput
              label="Zip"
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-zip-input"
              {...form.getInputProps("zip")}
              className={styles.field}
            />

            <TextInput
              label="Fax"
              withAsterisk={false}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="appeal-destination-fax-input"
              {...form.getInputProps("fax")}
              className={styles.field}
            />
          </div>
        </div>
      </form>
    </>
  );
});

AppealDestinationForm.displayName = "AppealDestinationForm";

export default AppealDestinationForm;
